import {
    LayerComponentState
} from "../../apps/containers/AppContainer/BarrelContainer/LayerDetailContainer/calculateLayerComponentState";
import { RemPreferences } from "../api/model";
import AIReview from "../api/model/AIReview/AIReview";
import AIReviewDot, { TransformedAIReviewDot } from "../api/model/AIReview/AIReviewDot";
import Approval from "../api/model/approvals/Approval";
import ScreenSection from "../api/model/barrels/ScreenSection";
import Tag from "../api/model/barrels/Tag";
import ApiColor from "../api/model/colors/Color";
import ComponentPage, { ComponentPageType, ComponentSectionType } from "../api/model/components/ComponentPage";
import ComponentSectionApiType from "../api/model/components/ComponentSection";
import ComponentVersion from "../api/model/components/ComponentVersion";
import ConnectedComponent from "../api/model/connectedComponents/ConnectedComponent";
import { AssetContentFormat } from "../api/model/snapshots/Asset";
import Rect from "../api/model/snapshots/layers/Rect";
import User from "../api/model/users/User";
import Asset, { AssetFormats, AssetFormatVariant, TransformedAsset } from "../model/Asset";
import Blur from "../model/blur";
import Border from "../model/border";
import Color from "../model/color";
import ColorFormat from "../model/ColorFormat";
import ColorStop from "../model/colorStop";
import ComponentSectionModelType from "../model/ComponentSection";
import CornerRadius from "../model/CornerRadius";
import Fill from "../model/fill";
import Font from "../model/font";
import Gradient from "../model/gradient";
import IndividualThickness from "../model/IndividualThickness";
import InspectLayer from "../model/InspectLayer";
import Layer from "../model/layer";
import Layout from "../model/Layout";
import Screen from "../model/Screen";
import { CompleteScreenVersion } from "../model/ScreenVersion";
import Shadow from "../model/shadow";
import { Story } from "../model/Story";
import StyleguideData from "../model/StyleguideData";
import {
    GroupedTextStyle,
    TransformedAIReview,
    TransformedApproval,
    TransformedAuthor,
    TransformedBlur,
    TransformedBorder,
    TransformedColor,
    TransformedCornerRadius,
    TransformedFill,
    TransformedGradient,
    TransformedIndividualThickness,
    TransformedLayer,
    TransformedLayerRect,
    TransformedLayout,
    TransformedRadius,
    TransformedShadow,
    TransformedTextStyle,
    TransformedThickness,
    TransformedTypeface
} from "../model/transform";
import UserModel from "../model/User";
import { PLATFORM, PlatformContextMap } from "../enums";

import { isSupportedAssetContent } from "./asset";
import BasicRecord from "./BasicRecord";
import { getComponentStyleguide } from "./component";
import formatter from "./formatter";
import { ImageLightness } from "./image";
import { sortByOrder } from "./indexing";
import { getDensityScale, getMostConformingTextStyle } from "./legacy";
import { round, toRemSize } from "./math";
import { exists } from "./object";
import { sortBy } from "./sortBy";
import { calculateStoryStatus } from "./story";
import { flatTree, generateTreeFromArray } from "./tree";

interface MinMaxValues{
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
}

function colorStopToCSS(colorStop: ColorStop, colorFormat?: string): string {
    const pos = colorStop.position > 0 && colorStop.position < 1 ? "" : ` ${Math.round(colorStop.position * 100)}%`;

    return `${colorStop.color.toCSS(colorFormat)}${pos}`;
}

function gradientToCss(gradient: Gradient, colorFormat?: string): string {
    return `linear-gradient(to bottom, ${gradient.colorStops.map(cs => colorStopToCSS(cs, colorFormat)).join(", ")})`;
}

function getTextStyleColorGroup(groupIndex: number): string {
    const colorGroups = [
        "turquoise",
        "cornflower-blue",
        "green",
        "yellow",
        "peach"
    ];

    /* @ts-ignore */ /** {@link parseInt} allows number parameters, its type definition is wrong */
    return colorGroups[parseInt(groupIndex, 10)] ?? `group-${groupIndex}`;
}

function groupTextStyles(layer: Layer): GroupedTextStyle[] {
    const defaultTextStyle = layer.getDefaultTextStyle()!;
    const textStyles: GroupedTextStyle[] = [];
    let unique = 0;
    const isCornflowerBlueUsed = () => textStyles.some(textStyle => textStyle.group === "cornflower-blue");

    layer.textStyles.forEach(({ font, range, hyperlink }) => {
        const found = textStyles.find(textStyle => textStyle.font.equals(font));
        let content;
        let group;

        if (defaultTextStyle.equals(font)) {
            // Default text style gets no color group
            group = "default";
        } else if (found) {
            group = found.group; // eslint-disable-line prefer-destructuring
        } else if (hyperlink && !isCornflowerBlueUsed()) {
            // Reserve "cornflower-blue" for the first hyperlink text style so links have a consistent color, unless it was already assigned sequentially.
            group = "cornflower-blue";
            content = layer.content.substring(range.start, range.end);
        } else {
            group = getTextStyleColorGroup(unique);
            // Skip "cornflower-blue" if it was used by a hyperlink.
            if (group === "cornflower-blue" && isCornflowerBlueUsed()) {
                unique += 1;
                group = getTextStyleColorGroup(unique);
            }

            content = layer.content.substring(range.start, range.end);
            unique += 1;
        }

        content = layer.content.substring(range.start, range.end);

        textStyles.push({
            font, range, group, content, hyperlink
        });
    });
    return textStyles;
}

function transformAsset(asset: Asset, { platform }: {
    platform: PLATFORM;
}): TransformedAsset | null {
    const {
        statState,
        localName,
        displayName,
        layerName,
        autoExported,
        contents,
        percent,
        size,
        lightness,
        _id: id
    } = asset;
    const { dimensionSuffix } = PlatformContextMap[platform];

    const previewImages: BasicRecord = {};
    const formats = {} as AssetFormats;

    const assetData: TransformedAsset = {
        statState,
        previewImages,
        displayName,
        layerName,
        localName,
        autoExported,
        formats,
        percent,
        light: lightness === ImageLightness.Light,
        id
    };

    const supportedContents = contents.filter(content => isSupportedAssetContent(platform, content));

    for (const content of supportedContents) {
        const {
            _id,
            density,
            densityScale = getDensityScale(density),
            format,
            optimized,
            url
        } = content;

        const variant: AssetFormatVariant = {
            _id,
            url,
            densityScale,
            optimized
        };

        if (format in formats) {
            formats[format].variants.push(variant);
        } else {
            formats[format] = { variants: [variant] };
        }

        if ("diff" in formats[format]) {
            continue; // eslint-disable-line no-continue
        } else if (percent && percent.type === format) {
            formats[format].diff = {
                type: percent.type === AssetContentFormat.Jpg ? AssetContentFormat.Png : AssetContentFormat.Jpg,
                percent: percent.diff
            };
        } else if (optimized && !optimized.originalURL && optimized.percent) {
            formats[format].diff = {
                type: AssetContentFormat.Svg,
                percent: optimized.percent
            };
        }
    }

    if (size) {
        assetData.size = {
            width: formatter.withUnit(size.width, dimensionSuffix),
            height: formatter.withUnit(size.height, dimensionSuffix)
        };
    }

    if (!(AssetContentFormat.Png in formats) && !(AssetContentFormat.Svg in formats)) {
        return null;
    }

    if (AssetContentFormat.Svg in formats) {
        previewImages[AssetContentFormat.Svg] = formats[AssetContentFormat.Svg].variants[0].url;
    }

    if (AssetContentFormat.Png in formats) {
        formats[AssetContentFormat.Png].variants.forEach(variant => {
            previewImages[variant.densityScale] = variant.url;
        });
    }

    return assetData;
}

function transformAssets(
    assets: Asset[] | undefined,
    transformSettings: {
        platform: PLATFORM;
    }
): TransformedAsset[] {
    return assets?.map(asset => transformAsset(asset, transformSettings)).filter(exists) ?? [];
}

function transformAuthors(
    versions: (CompleteScreenVersion | ComponentVersion)[] | undefined
): TransformedAuthor[] {
    if (!versions) {
        return [];
    }

    const groupedVersions = versions.reduce<BasicRecord<(CompleteScreenVersion | ComponentVersion)[]>>((groups, v) => {
        const creatorId = v.creator?._id || "anonymous";

        groups[creatorId] = (groups[creatorId] ?? []).concat(v);

        return groups;
    }, {});

    return Object.values(groupedVersions).map(vs => {
        vs.sort(sortBy.created);

        const [{ creator, created }] = vs;

        return {
            author: creator,
            versionCount: vs.length,
            lastUpdated: created
        };
    });
}

function transformBlur(blur: Blur, { densityScale, dimensionSuffix }: {
    densityScale: number;
    dimensionSuffix: string;
}): TransformedBlur {
    const { type, radius, variableReferences } = blur;

    return {
        type,
        radius: formatter.length(radius, densityScale, dimensionSuffix),
        variableReferences
    };
}

function transformThickness(
    thickness: number,
    individualThickness: IndividualThickness | undefined,
    densityScale: number,
    dimensionSuffix: string): TransformedThickness {
    if (individualThickness) {
        return {
            individualThickness: transformIndividualThickness(individualThickness, densityScale, dimensionSuffix)
        };
    }

    return {
        singleThicknessTransformed: formatter.length(thickness, densityScale, dimensionSuffix)
    };
}

function transformIndividualThickness(
    individualThickness: IndividualThickness,
    densityScale: number,
    dimensionSuffix: string): TransformedIndividualThickness {
    return {
        top: formatter.unit(individualThickness.top, densityScale),
        right: formatter.unit(individualThickness.right, densityScale),
        bottom: formatter.unit(individualThickness.bottom, densityScale),
        left: formatter.unit(individualThickness.left, densityScale),
        dimensionSuffix
    };
}

function transformBorder(
    border: Border,
    styleguidesData: StyleguideData[],
    { platform, colorFormat, densityScale }: {
        platform: PLATFORM;
        colorFormat?: ColorFormat;
        densityScale: number;
    }
): TransformedBorder {
    const {
        fill: { color, gradient }, position, thickness, individualThickness, variableReferences
    } = border;
    const { dimensionSuffix } = PlatformContextMap[platform];
    const transformedThickness = transformThickness(
        thickness, individualThickness, densityScale, dimensionSuffix
    );

    return {
        color: color && transformColor(color, styleguidesData, { platform, colorFormat }),
        gradient: gradient && transformGradient(gradient, styleguidesData, { platform, colorFormat }),
        position,
        thickness: transformedThickness,
        variableReferences
    };
}

function transformRadius(
    borderRadius: number,
    cornerRadius: CornerRadius | undefined,
    densityScale: number,
    dimensionSuffix: string): TransformedRadius | null {
    if (cornerRadius) {
        return {
            cornerRadiusTransformed: transformCornerRadius(cornerRadius, densityScale, dimensionSuffix)
        };
    }

    if (borderRadius > 0) {
        return {
            borderRadiusTransformed: formatter.length(borderRadius, densityScale, dimensionSuffix)
        };
    }

    return null;
}

function transformCornerRadius(
    cornerRadius: CornerRadius,
    densityScale: number,
    dimensionSuffix: string): TransformedCornerRadius {
    return {
        topLeft: formatter.unit(cornerRadius.topLeft, densityScale),
        topRight: formatter.unit(cornerRadius.topRight, densityScale),
        bottomRight: formatter.unit(cornerRadius.bottomRight, densityScale),
        bottomLeft: formatter.unit(cornerRadius.bottomLeft, densityScale),
        dimensionSuffix
    };
}

function transformColor(color: Color, colorsData: StyleguideData[], { platform, colorFormat }: {
    platform: PLATFORM;
    colorFormat?: ColorFormat;
}): TransformedColor {
    const value: ApiColor = {
        sourceId: color.sourceId,
        name: color.name,
        r: color.r,
        g: color.g,
        b: color.b,
        a: color.a
    };

    let fallbackColor: TransformedColor | undefined;
    const colorStyle = color.toCSS();
    const colorString = color.toFormattedString({ colorFormat, platform });

    for (const {
        id: styleguideId,
        name: styleguideName,
        colors,
        isMember,
        isOrganizationBarrel
    } of colorsData) {
        const foundColors = colors!.filter(c => c.equals(color));

        for (const foundColor of foundColors) {
            const transformedColor: TransformedColor = {
                styleguideId,
                styleguideName,
                isMember,
                isOrganizationBarrel,
                added: true,
                id: foundColor.id,
                value: {
                    sourceId: color.sourceId,
                    name: foundColor.name,
                    r: color.r,
                    g: color.g,
                    b: color.b,
                    a: color.a
                },
                colorStyle,
                colorString,
                name: foundColor.name,
                exactMatch: color.toRGBAString() === foundColor.toRGBAString(),
                opacity: formatter.alpha(color.a)
            };

            if (!foundColor.sourceId && !!color.sourceId) {
                fallbackColor ??= transformedColor;
            } else {
                return transformedColor;
            }
        }

        if (fallbackColor) {
            return fallbackColor;
        }
    }

    for (const {
        id: styleguideId,
        name: styleguideName,
        colors,
        isMember,
        isOrganizationBarrel
    } of colorsData) {
        const similarColor = colors!.find(c => c.hexBase() === color.hexBase());

        if (similarColor?.a === 1 && !(color.sourceId && similarColor.sourceId)) {
            const { id, name } = similarColor;

            return {
                styleguideId,
                styleguideName,
                isMember,
                isOrganizationBarrel,
                added: false,
                id,
                value,
                colorStyle,
                colorString,
                name,
                opacity: formatter.alpha(color.a),
                exactMatch: true
            };
        }
    }

    return {
        added: false,
        value,
        colorStyle,
        colorString,
        opacity: formatter.alpha(color.a),
        exactMatch: true
    };
}

function transformGradient(gradient: Gradient, styleguidesData: StyleguideData[],
    {
        platform, colorFormat
    }: {
        platform: PLATFORM;
        colorFormat?: ColorFormat;
    }): TransformedGradient {
    return {
        type: gradient.type,
        gradientStyle: gradientToCss(gradient),
        colorStops: gradient.colorStops.map(({ color, position, variableReferences }) => ({
            position: round(position, 2),
            color: transformColor(color, styleguidesData, { platform, colorFormat }),
            variableReferences
        }))
    };
}

function transformShadow(shadow: Shadow, colorsData: StyleguideData[], {
    platform,
    colorFormat,
    densityScale
}: {
    platform: PLATFORM;
    colorFormat?: ColorFormat;
    densityScale: number;
}): TransformedShadow {
    const {
        blurRadius,
        color,
        offsetX,
        offsetY,
        spread,
        type,
        variableReferences
    } = shadow;
    const { dimensionSuffix } = PlatformContextMap[platform];

    return {
        blurRadius: formatter.length(blurRadius, densityScale, dimensionSuffix),
        color: transformColor(color, colorsData, { platform, colorFormat }),
        offsetX: formatter.length(offsetX, densityScale, dimensionSuffix),
        offsetY: formatter.length(offsetY, densityScale, dimensionSuffix),
        spread: formatter.length(spread, densityScale, dimensionSuffix),
        type,
        variableReferences
    };
}

// eslint-disable-next-line complexity
function transformTextStyle(
    textStyle: Font,
    styleguidesData: StyleguideData[],
    {
        platform, colorFormat, densityScale, remPreferences
    }: {
        platform: PLATFORM;
        colorFormat?: ColorFormat;
        densityScale: number;
        remPreferences: RemPreferences;
    }): TransformedTextStyle {
    const conformingTextStyle = getMostConformingTextStyle(styleguidesData, textStyle, densityScale);
    const {
        color,
        fontFace,
        fontSize,
        letterSpacing,
        lineHeight,
        textAlign,
        fontVariationSettings,
        textDecoration,
        textTransform,
        paragraphSpacing,
        paragraphIndent,
        listSpacing,
        verticalAlignment,
        variableReferences
    } = textStyle;
    const isRemEnabledForFontSize =
        platform === PLATFORM.WEB && remPreferences.status === "enabled" && remPreferences.useForFontSizes;

    const transformedColor = color && transformColor(color, styleguidesData, { platform, colorFormat });

    const {
        id,
        styleguideId,
        styleguideName,
        isMember,
        isOrganizationBarrel
    } = conformingTextStyle ?? {};

    const { textDimensionSuffix } = PlatformContextMap[platform];

    return {
        styleguideId,
        styleguideName,
        isMember,
        isOrganizationBarrel,
        id,
        added: !!conformingTextStyle,
        color: transformedColor,
        name: conformingTextStyle && conformingTextStyle.name,
        fontFace,
        fontSize: isRemEnabledForFontSize
            ? toRemSize(fontSize / densityScale, remPreferences.remRootFontSize!)
            : formatter.textLength(fontSize, densityScale, textDimensionSuffix),
        letterSpacing: letterSpacing && formatter.letterSpacing(letterSpacing, densityScale, textDimensionSuffix),
        paragraphSpacing: paragraphSpacing && (
            isRemEnabledForFontSize
                ? toRemSize(paragraphSpacing / densityScale, remPreferences.remRootFontSize!)
                : formatter.textLength(paragraphSpacing, densityScale, textDimensionSuffix)
        ),
        listSpacing: listSpacing && (
            isRemEnabledForFontSize
                ? toRemSize(listSpacing / densityScale, remPreferences.remRootFontSize!)
                : formatter.textLength(listSpacing, densityScale, textDimensionSuffix)
        ),
        paragraphIndent: paragraphIndent && (
            isRemEnabledForFontSize
                ? toRemSize(paragraphIndent / densityScale, remPreferences.remRootFontSize!)
                : formatter.textLength(paragraphIndent, densityScale, textDimensionSuffix)
        ),
        verticalAlignment: verticalAlignment !== "top" ? verticalAlignment : undefined,
        lineHeight: lineHeight && (
            isRemEnabledForFontSize
                ? toRemSize(lineHeight / densityScale, remPreferences.remRootFontSize!)
                : formatter.textLength(lineHeight, densityScale, textDimensionSuffix)
        ),
        textAlign: textAlign !== "left" ? textAlign : undefined,
        textDecoration,
        textTransform,
        fontVariationSettings,
        value: {
            color: transformedColor && transformedColor.value,
            fontFace,
            fontSize: fontSize / densityScale,
            letterSpacing: letterSpacing && letterSpacing / densityScale,
            paragraphSpacing: paragraphSpacing && paragraphSpacing / densityScale,
            lineHeight: lineHeight && lineHeight / densityScale,
            textAlign: textAlign !== "left" ? textAlign : undefined,
            paragraphIndent,
            listSpacing,
            verticalAlignment: verticalAlignment !== "top" ? verticalAlignment : undefined,
            fontVariationSettings,
            textDecoration,
            textTransform
        },
        variableReferences
    };
}

function transformTypefaces(
    layer: Layer,
    styleguidesData: StyleguideData[],
    {
        platform, colorFormat, densityScale, remPreferences
    }: {
        platform: PLATFORM;
        colorFormat?: ColorFormat;
        densityScale: number;
        remPreferences: RemPreferences;
    }
): TransformedTypeface[] {
    if (layer.type !== "text") {
        return [];
    }

    return groupTextStyles(layer).map(({
        font, group, content, range, hyperlink
    }) => ({
        textStyle: transformTextStyle(
            font,
            styleguidesData,
            {
                platform, colorFormat, densityScale, remPreferences
            }),
        group,
        content,
        hyperlink,
        range
    }));
}

function transformLayerFill(fill: Fill, styleguidesData: StyleguideData[], { platform, colorFormat }: {
    platform: PLATFORM;
    colorFormat?: ColorFormat;
}): TransformedFill {
    const fillData: TransformedFill = { type: fill.type, variableReferences: fill.variableReferences };

    if (fill.type === "color") {
        fillData.color = transformColor(fill.color!, styleguidesData, { platform, colorFormat });
    } else if (fill.type === "gradient") {
        fillData.gradient = transformGradient(fill.gradient!, styleguidesData, { platform, colorFormat });
    }

    if (fill.blendMode && fill.blendMode !== "normal") {
        fillData.blendMode = fill.blendMode;
    }

    return fillData;
}

function transformLayerRect(
    rect: Rect,
    { platform, densityScale, remPreferences }: {
        platform: PLATFORM;
        densityScale: number;
        remPreferences: RemPreferences;
    },
    minMaxValues?: MinMaxValues
): TransformedLayerRect {
    const {
        status: remStatus,
        useForMeasurements,
        remRootFontSize
    } = remPreferences;
    const isRemEnabledForMeasurements = platform === PLATFORM.WEB && remStatus === "enabled" && useForMeasurements;
    const { dimensionSuffix } = PlatformContextMap[platform];
    const transformedRect: TransformedLayerRect = isRemEnabledForMeasurements
        ? {
            x: toRemSize(rect.x / densityScale, remRootFontSize!),
            y: toRemSize(rect.y / densityScale, remRootFontSize!),
            width: toRemSize(rect.width / densityScale, remRootFontSize!),
            height: toRemSize(rect.height / densityScale, remRootFontSize!)
        }
        : {
            x: formatter.length(rect.x, densityScale, dimensionSuffix),
            y: formatter.length(rect.y, densityScale, dimensionSuffix),
            width: formatter.length(rect.width, densityScale, dimensionSuffix),
            height: formatter.length(rect.height, densityScale, dimensionSuffix)
        };

    if (minMaxValues) {
        // eslint-disable-next-line guard-for-in
        for (const maximumPointKey in minMaxValues) {
            const maximumPointValue = minMaxValues[(maximumPointKey as keyof MinMaxValues)];

            if (maximumPointValue) {
                transformedRect[(maximumPointKey as keyof MinMaxValues)] = isRemEnabledForMeasurements
                    ? toRemSize(maximumPointValue / densityScale, remRootFontSize!)
                    : formatter.length(maximumPointValue, densityScale, dimensionSuffix);
            }
        }
    }

    return transformedRect;
}

function transformLayout(layout: Layout, { platform, densityScale, remPreferences }: {
    platform: PLATFORM;
    densityScale: number;
    remPreferences: RemPreferences;
}): TransformedLayout {
    const {
        status: remStatus,
        useForMeasurements,
        remRootFontSize
    } = remPreferences;
    const { dimensionSuffix } = PlatformContextMap[platform];
    const isRemEnabledForMeasurements = platform === PLATFORM.WEB && remStatus === "enabled" && useForMeasurements;
    const { padding, gap } = layout;
    let patch: Partial<TransformedLayout> = {};

    if (padding && padding.top! >= 0) {
        patch = {
            padding: {
                top: round(padding.top! / densityScale, 1),
                right: round(padding.right! / densityScale, 1),
                bottom: round(padding.bottom! / densityScale, 1),
                left: round(padding.left! / densityScale, 1)
            }
        };

        if (isRemEnabledForMeasurements) {
            patch = {
                ...patch,
                formattedPadding: {
                    top: toRemSize(padding.top! / densityScale, remRootFontSize!),
                    right: toRemSize(padding.right! / densityScale, remRootFontSize!),
                    bottom: toRemSize(padding.bottom! / densityScale, remRootFontSize!),
                    left: toRemSize(padding.left! / densityScale, remRootFontSize!)
                }
            };
        } else {
            patch = {
                ...patch,
                formattedPadding: {
                    top: formatter.length(padding.top!, densityScale, dimensionSuffix),
                    right: formatter.length(padding.right!, densityScale, dimensionSuffix),
                    bottom: formatter.length(padding.bottom!, densityScale, dimensionSuffix),
                    left: formatter.length(padding.left!, densityScale, dimensionSuffix)
                }
            };
        }
    }

    if (gap) {
        if (gap === -3) {
            patch = {
                ...patch,
                formattedGap: "auto"
            };
        } else if (gap === -2) {
            patch = {
                ...patch,
                formattedGap: "mixed"
            };
        } else if (gap === Number.MAX_VALUE) {
            patch = {
                ...patch,
                formattedGap: "Infinity"
            };
        } else if (gap >= 0) {
            patch = {
                ...patch,
                gap: round(gap / densityScale, 1),
                formattedGap: isRemEnabledForMeasurements
                    ? toRemSize(gap / densityScale, remRootFontSize!)
                    : formatter.length(gap, densityScale, dimensionSuffix)
            };
        }
    }

    return { ...layout, ...patch };
}

function transformLayer(layer: Layer, {
    platform, colorFormat, densityScale, remPreferences, styleguidesData,
    component, virtualComponentLayer, assetParent, versionAsyncAssetsProgress
}: {
    platform: PLATFORM;
    colorFormat?: ColorFormat;
    densityScale: number;
    remPreferences: RemPreferences;
    styleguidesData: StyleguideData[];
    component: LayerComponentState | null;
    virtualComponentLayer: InspectLayer | null;
    assetParent: InspectLayer | null;
    versionAsyncAssetsProgress: number | undefined;
}) {
    const {
        _id,
        id = _id,
        absoluteRect,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        assetData,
        blendMode,
        blur,
        borders,
        borderRadius,
        cornerRadius,
        content,
        fills,
        name,
        opacity,
        rotation,
        shadows,
        type,
        styleName,
        textStyleName,
        constraints,
        layout,
        fromLibrary,
        libraryName,
        variableReferences,
        exportable,
        fillStyle,
        borderStyle,
        effectStyle
    } = layer;

    const minMaxValues = {
        minWidth,
        maxWidth,
        minHeight,
        maxHeight
    };

    const { dimensionSuffix, textDimensionSuffix } = PlatformContextMap[platform];
    const layerData: TransformedLayer = {
        id,
        borders: borders.map(
            border => transformBorder(border, styleguidesData, { platform, colorFormat, densityScale })
        ),
        component,
        virtualComponentLayer,
        content,
        fills: fills.map(fill => transformLayerFill(fill, styleguidesData, { platform, colorFormat })),
        name,
        rect: transformLayerRect(
            absoluteRect!,
            { platform, densityScale, remPreferences },
            minMaxValues
        ),
        shadows: shadows.map(
            shadow => transformShadow(shadow, styleguidesData, { platform, colorFormat, densityScale })
        ),
        type,
        typefaces: transformTypefaces(
            layer,
            styleguidesData,
            {
                platform, colorFormat, densityScale, remPreferences
            }
        ),
        styleName,
        textStyleName,
        fromLibrary,
        libraryName,
        dimensionSuffix,
        textDimensionSuffix,
        variableReferences,
        exportable,
        versionAsyncAssetsProgress,
        fillStyle,
        borderStyle,
        effectStyle
    };

    if (assetData) {
        layerData.asset = transformAsset(assetData as Asset, { platform });
        layerData.assetParent = assetParent;
    }

    if (blur) {
        layerData.blur = transformBlur(blur, { densityScale, dimensionSuffix });
    }

    if (opacity < 1) {
        layerData.opacity = formatter.percentage(opacity);
    }

    layerData.radius = transformRadius(borderRadius, cornerRadius, densityScale, dimensionSuffix);

    if (rotation !== 0) {
        layerData.rotation = rotation;
    }

    if (blendMode !== "normal") {
        layerData.blendMode = blendMode;
    }

    if (constraints) {
        layerData.constraints = constraints;
    }

    if (layout) {
        layerData.layout = transformLayout(layout, { platform, densityScale, remPreferences });
    }

    if (component) {
        layerData.componentStyleguide = getComponentStyleguide(component.componentId, styleguidesData);
    }

    return layerData;
}

function transformStories({
    storybookUrl,
    stories,
    styleguidesData
}: {
    storybookUrl: string;
    stories: Partial<Story>[] | undefined;
    styleguidesData: StyleguideData[];
}): Story[] {
    if (!stories) {
        return [];
    }

    const storiesWithoutDocs = stories.filter(story => story.type !== "docs");

    const missingStories: Partial<Story>[] = [];
    let computedStories: Partial<Story>[] = [...storiesWithoutDocs];

    const connectedComponentItemsWithStory =
        styleguidesData.reduce<ConnectedComponent[]>((acc, { connectedComponents = [] }) => {
            const connectedComponentItems = connectedComponents!.filter(
                ({ links }) => links?.some(({ metadata }) => metadata?.storybook)
            );

            return acc.concat(connectedComponentItems);
        }, []);

    for (const connectedComponentItemWithStory of connectedComponentItemsWithStory) {
        const missingStoryLinks = connectedComponentItemWithStory.links!.filter(({ metadata }) =>
            metadata?.storybook && !storiesWithoutDocs.find(
                story => `${metadata.storybook!.kind}/${metadata.storybook!.story}` === `${story.kind}/${story.name}`
            )
        );

        for (const missingStoryLink of missingStoryLinks) {
            const storybook = missingStoryLink.metadata!.storybook!;
            const missingStoryId = `${storybook.kind}--${storybook.story}`;
            const doesStoryExist = missingStories.some(story => story.id === missingStoryId);

            if (!doesStoryExist) {
                missingStories.push({
                    id: missingStoryId,
                    url: missingStoryLink.url,
                    kind: storybook.kind,
                    originalKind: storybook.kind,
                    name: storybook.story,
                    zeplinSources: [],
                    isMissing: true
                });
            }
        }
    }

    computedStories = [...computedStories, ...missingStories];

    return computedStories.map(story => {
        const matchedConnectedComponentItems =
            connectedComponentItemsWithStory.filter(({ links }) => links!.some(({ metadata }) =>
                metadata?.storybook &&
                    `${metadata.storybook.kind}/${metadata.storybook.story}` === `${story.kind}/${story.name}`
            ));
        const connectedComponentItemIds = matchedConnectedComponentItems.map(({ _id }) => _id);
        const connectedComponentIds = matchedConnectedComponentItems
            .map(({ componentId }) => componentId)
            .filter((componentId, index, self) => self.indexOf(componentId) === index) as string[];
        const status = calculateStoryStatus(connectedComponentIds, styleguidesData);

        return {
            ...story,
            url: story.url || `${storybookUrl}/?path=/story/${story.id}`,
            kind: story.isMissing ? "Missing stories" : story.kind,
            status,
            connectedComponentItemIds,
            connectedComponentIds,
            zeplinSources: []
        } as Story;
    });
}

// TODO-bedo: Convert this to a generic transformSections util
function transformScreenSections(sections: ScreenSection[], identifier = "_id") {
    return flatTree({
        tree: generateTreeFromArray({ array: sections as unknown as BasicRecord[], identifier, childrenKey: "sections", parentKey: "parent" }),
        identifier,
        childrenKey: "sections"
    });
}

function transformProjectTags(tags: Tag[], screens: Screen[]) {
    const screenTagIds = screens.flatMap(screen => screen.tags);
    const tagIdScreenCountMap: Map<string, number> = new Map();

    for (const screenTagId of screenTagIds) {
        const tagScreenCount = tagIdScreenCountMap.get(screenTagId) || 0;

        tagIdScreenCountMap.set(screenTagId, tagScreenCount + 1);
    }

    return tags.map(tag => {
        tag.screenCount = tagIdScreenCountMap.get(tag._id) || 0;

        return tag;
    });
}

function transformApprovals(approvals: Approval[]): TransformedApproval[] {
    return approvals.flatMap(approval => {
        const { reviews = [], ...rest } = approval;
        return reviews.map(review => ({
            ...rest,
            review
        }));
    });
}

function transformAIReview(aiReview: AIReview): TransformedAIReview {
    return {
        ...aiReview,
        review: {
            requestedBy: aiReview.requestedBy ? aiReview.requestedBy : window.Zeplin.user,
            reviewedBy: window.Zeplin.user,
            status: aiReview.status
        },
        dots: aiReview.screens.flatMap(screen => screen.dots ?? [])
    };
}

function transformAIReviews(aiReviews?: AIReview[]): TransformedAIReview[] | undefined {
    return aiReviews?.map(transformAIReview);
}

function transformAIReviewDots(aiReviewDots: AIReviewDot[], {
    createdAt,
    updatedAt,
    requestedBy
}: {
    createdAt: string;
    updatedAt: string;
    requestedBy: User | null;
}) {
    const author = requestedBy ? new UserModel(requestedBy) : window.Zeplin.user;

    return aiReviewDots.map(({ comment, ...dotData }) => ({
        ...dotData,

        creator: author,
        comments: [{
            _id: dotData._id,
            note: comment,
            author,
            created: createdAt,
            updated: updatedAt,
            isUnread: false
        }]
    }));
}

function transformStyleguideComponentSections<T = ComponentSectionApiType>(
    componentSections: (ComponentSectionModelType | ComponentSectionApiType)[] = [],
    pages: ComponentPage[] = []
): T[] {
    const pageSections = new Set(pages.flatMap(page => page.componentSections.map(cs => cs.id)));

    const legacySectionsTransformed = componentSections
        .filter(componentSection => !pageSections.has(componentSection._id))
        .map(componentSection => {
            const transformedSections = componentSection.componentSections.map(cs => ({
                ...cs,
                type: ComponentSectionType.pageSubsection,
                parentPageId: componentSection._id
            }));

            return {
                ...componentSection,
                type: ComponentPageType.legacy,
                componentSections: transformedSections.sort(sortByOrder)
            };
        });

    const pagesTransformed = pages
        ?.map(componentPage => ({
            ...componentPage,
            componentSections: componentPage.componentSections.map(({ id, order }) => {
                const correspondingSection = componentSections.find(({ _id }) => _id === id)!;

                return {
                    ...correspondingSection,
                    type: ComponentSectionType.pageSection,
                    order,
                    parentPageId: componentPage._id,
                    componentSections: correspondingSection?.componentSections?.map(cs => ({
                        ...cs,
                        type: ComponentSectionType.pageSubsection,
                        parentPageId: componentPage._id
                    })) ?? []
                };
            }).sort(sortByOrder),
            components: [],
            idPath: [componentPage._id]
        }));

    return [
        ...legacySectionsTransformed,
        ...pagesTransformed
    ] as unknown as T[];
}

function filterStyleguideSectionsByType(styguides: StyleguideData[], filterType?: ComponentPageType): StyleguideData[] {
    const filterIcons = filterType === ComponentPageType.icon;

    return styguides.map(styleguide => ({
        ...styleguide,
        componentSections: styleguide.componentSections
            ?.filter(({ type }) => (filterIcons ? type === ComponentPageType.icon : type !== ComponentPageType.icon))
    }));
}

function transformDotsToCategories(dots?: TransformedAIReviewDot[]) {
    const dotsToRender: BasicRecord<TransformedAIReviewDot[]> = {};

    if (!dots) {
        return;
    }

    dots.forEach(dot => {
        if (!dotsToRender[dot.category]) {
            dotsToRender[dot.category] = [dot];
        } else {
            dotsToRender[dot.category].push(dot);
        }
    });

    return dotsToRender;
}

export {
    transformAssets,
    transformAuthors,
    transformColor,
    transformLayer,
    transformLayerRect,
    transformStories,
    transformScreenSections,
    transformProjectTags,
    transformApprovals,
    transformAIReview,
    transformAIReviews,
    transformAIReviewDots,
    transformDotsToCategories,
    transformStyleguideComponentSections,
    filterStyleguideSectionsByType
};
